import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import firebase, { rsf } from '../../components/library/firebase/firebase';
import { getAccountIdPrefix, getLoggedUserId } from '../../components/library/helpers/local_storage';
import { simpleQueryFirestoreWithFilterOrderBy } from '../../components/utility/firestore.helper';
import { validateFile, buildStoragePath } from '../../components/library/documents/document.model';
import actions from './actions';

const COLLECTION_NAME_DOCUMENTS = 'client_documents';
const COLLECTION_NAME_FOLDERS = 'document_folders';

export function* loadDocuments(query: any) {
  try {
    const clientId = query.payload.clientId;
    const filters = [{ field: 'clientId', operator: '==', value: clientId }];
    const orderBy = { field: 'createdAt', type: 'desc' };
    const data = yield call(
      simpleQueryFirestoreWithFilterOrderBy,
      getAccountIdPrefix() + COLLECTION_NAME_DOCUMENTS,
      filters,
      orderBy
    );
    // simpleQueryFirestoreWithFilterOrderBy returns a map keyed by doc id;
    // the doc id lives in the key (data() does not include it), so inject it.
    const documents = Object.keys(data).map((key) => ({
      ...data[key],
      id: key,
    }));
    yield put(actions.loadDocumentsSuccess(documents));
  } catch (error: any) {
    yield put(actions.loadDocumentsError(error && error.message ? error.message : String(error)));
  }
}

export function* uploadDocument(query: any) {
  try {
    const { clientId, file, folderId } = query.payload;
    const validation = validateFile({ type: file.type, size: file.size });
    if (!validation.ok) {
      yield put(actions.uploadDocumentError(validation.error));
      return;
    }

    const ts = Date.now();
    const path = buildStoragePath(getAccountIdPrefix(), clientId, ts, file.name);

    yield call(rsf.storage.uploadFile, path, file);

    const doc: any = {
      clientId,
      folderId: folderId || null,
      name: file.name,
      contentType: file.type,
      size: file.size,
      storagePath: path,
      createdAt: ts,
      uploadedBy: getLoggedUserId(),
    };

    // rsf.firestore.addDocument resolves to a DocumentReference; its .id is the new doc id
    const ref = yield call(rsf.firestore.addDocument, getAccountIdPrefix() + COLLECTION_NAME_DOCUMENTS, doc);
    doc.id = ref.id;

    yield put(actions.uploadDocumentSuccess(doc));
  } catch (error: any) {
    yield put(actions.uploadDocumentError(error && error.message ? error.message : String(error)));
  }
}

export function* deleteDocument({ payload }: any) {
  const { id, storagePath } = payload;
  try {
    yield call(rsf.firestore.deleteDocument, getAccountIdPrefix() + COLLECTION_NAME_DOCUMENTS + '/' + id);

    // rsf 0.15 has no deleteFile — remove the binary with the native storage API.
    // Wrap in its own try/catch so a missing object does not fail the whole action.
    try {
      const storageRef = firebase.storage().ref(storagePath);
      yield call([storageRef, 'delete']);
    } catch (storageError) {
      // Binary already gone (or storage error) — the Firestore record was already removed,
      // so the DELETE_DOCUMENT action still succeeds.
    }

    yield put(actions.deleteDocumentSuccess(id));
  } catch (error: any) {
    yield put(actions.deleteDocumentError(error && error.message ? error.message : String(error)));
  }
}

export function* loadFolders(query: any) {
  try {
    const clientId = query.payload.clientId;
    // Single-field equality filter, NO orderBy: this hits the automatic single-field
    // index, so it requires no composite index. parentId filtering and name sorting
    // are done in the client (UI), not in the query.
    const filters = [{ field: 'clientId', operator: '==', value: clientId }];
    const data = yield call(
      simpleQueryFirestoreWithFilterOrderBy,
      getAccountIdPrefix() + COLLECTION_NAME_FOLDERS,
      filters
    );
    const folders = Object.keys(data).map((key) => ({
      ...data[key],
      id: key,
    }));
    yield put(actions.loadFoldersSuccess(folders));
  } catch (error: any) {
    yield put(actions.loadFoldersError(error && error.message ? error.message : String(error)));
  }
}

export function* createFolder(query: any) {
  try {
    const { clientId, parentId, name } = query.payload;

    const doc: any = {
      clientId,
      parentId: parentId || null,
      name,
      // parentId is the source of truth for the hierarchy; `path` is a simple
      // convenience value (parent folder id, or '' at the root).
      path: parentId || '',
      createdAt: Date.now(),
    };

    // rsf.firestore.addDocument resolves to a DocumentReference; its .id is the new doc id
    const ref = yield call(rsf.firestore.addDocument, getAccountIdPrefix() + COLLECTION_NAME_FOLDERS, doc);
    doc.id = ref.id;

    yield put(actions.createFolderSuccess(doc));
  } catch (error: any) {
    yield put(actions.createFolderError(error && error.message ? error.message : String(error)));
  }
}

export function* deleteFolder({ payload }: any) {
  const { folder, allFolders, allDocuments } = payload;
  try {
    // 1) Compute the id of `folder` plus every descendant folder (BFS by parentId).
    const folderIds = new Set<string>([folder.id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of allFolders || []) {
        if (f.parentId && folderIds.has(f.parentId) && !folderIds.has(f.id)) {
          folderIds.add(f.id);
          grew = true;
        }
      }
    }

    // 2) Delete every document that lives in any of those folders (Firestore + storage binary).
    const docsToDelete = (allDocuments || []).filter(
      (d: any) => d.folderId && folderIds.has(d.folderId)
    );
    for (const d of docsToDelete) {
      yield call(rsf.firestore.deleteDocument, getAccountIdPrefix() + COLLECTION_NAME_DOCUMENTS + '/' + d.id);
      // rsf 0.15 has no deleteFile — remove the binary with the native storage API.
      // Wrap in its own try/catch so a missing object does not fail the whole action.
      try {
        const storageRef = firebase.storage().ref(d.storagePath);
        yield call([storageRef, 'delete']);
      } catch (storageError) {
        // Binary already gone (or storage error) — the Firestore record was already removed.
      }
    }

    // 3) Delete every folder in the subtree.
    for (const fid of Array.from(folderIds)) {
      yield call(rsf.firestore.deleteDocument, getAccountIdPrefix() + COLLECTION_NAME_FOLDERS + '/' + fid);
    }

    yield put(actions.deleteFolderSuccess(folder.id));
  } catch (error: any) {
    yield put(actions.deleteFolderError(error && error.message ? error.message : String(error)));
  }
}

export function* notifyClientUploadsSaga({ payload }: any) {
  try {
    const fn = firebase.functions().httpsCallable('notifyClientDocuments');
    yield call(fn, {
      clientId: payload.clientId,
      documents: payload.documents,
      subject: payload.subject,
      message: payload.message,
      to: payload.to,
    });
  } catch (e) {
    // best-effort: el email nunca rompe el flujo
  }
}

export default function* documentsSaga() {
  yield all([
    takeLatest(actions.LOAD_DOCUMENTS, loadDocuments),
    takeEvery(actions.UPLOAD_DOCUMENT, uploadDocument),
    takeEvery(actions.DELETE_DOCUMENT, deleteDocument),
    takeLatest(actions.LOAD_FOLDERS, loadFolders),
    takeEvery(actions.CREATE_FOLDER, createFolder),
    takeEvery(actions.DELETE_FOLDER, deleteFolder),
    takeEvery(actions.NOTIFY_CLIENT_UPLOADS, notifyClientUploadsSaga),
  ]);
}
